/**
 * SD-8.4 (FR-26, spec-fr26-module-library.md §Surface): the Manage modules dialog.
 *
 * A DIALOG opened from the Equipment card, deliberately NOT a new settings page — this dialog
 * manages the catalogue; the settings screen picks the default from it. The tenant default is
 * therefore not chosen here.
 *
 * SHIPPED VERSUS YOURS, the same language the rest of the screen speaks (UX-DR18): global rows
 * are read-only and badged `Shipped` — there is no edit path to them in the UI because there is
 * no RLS write policy matching them in the database, and a control that will 42501 is not a
 * control (the SD-8.3 rule). Tenant rows are editable and badged `Yours`.
 *
 * DEACTIVATE, NEVER DELETE (FR-20). The deactivate action states its reason in place —
 * "Layouts already built with this module keep using it." — because an unexplained deactivate
 * reads as a failed delete. A deactivated row STAYS IN THIS LIST (listModulesForManagement
 * includes inactive) with a Reactivate action; vanishing would be indistinguishable from the
 * deletion this table refuses to offer. No delete affordance exists here, and the service has
 * no delete function to call.
 *
 * ADD AND EDIT take manufacturer, model CODE, watts and integer millimetres — what a datasheet
 * publishes — and show the derived efficiency and feet-and-inches size alongside as the admin
 * types, since the audience thinks in both. Validation mirrors the 20260805g CHECKs via
 * moduleFacts, so the form refuses in words exactly what the database would refuse in SQLSTATEs;
 * the database stays the enforcer, and a write that still fails surfaces through
 * saveErrorMessage so a permission refusal keeps the server's own wording (SD-8.3).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EMPTY_AND_ERROR } from '@/lib/solarDesignCopy';
import {
  type PvModuleRow,
  type TenantModuleInput,
  createTenantModule,
  listModulesForManagement,
  setModuleActive,
  updateTenantModule,
} from '@/services/pvModulesService';
import { saveErrorMessage } from './designSettingsStates';
import {
  type ModuleInputErrors,
  formatEfficiency,
  physicalSize,
  validateModuleInput,
} from './moduleFacts';

export const DEACTIVATE_REASON = 'Layouts already built with this module keep using it.';

interface DraftFields {
  manufacturer: string;
  model: string;
  watts: string;
  width_mm: string;
  height_mm: string;
}

const EMPTY_DRAFT: DraftFields = {
  manufacturer: '',
  model: '',
  watts: '',
  width_mm: '',
  height_mm: '',
};

const draftFromRow = (row: PvModuleRow): DraftFields => ({
  manufacturer: row.manufacturer,
  model: row.model,
  watts: String(row.watts),
  width_mm: String(row.width_mm),
  height_mm: String(row.height_mm),
});

/** Bare numbers typed into a form → the integer the library stores. NaN survives to validation. */
const toModuleInput = (draft: DraftFields): TenantModuleInput => ({
  manufacturer: draft.manufacturer,
  model: draft.model,
  watts: Number(draft.watts),
  width_mm: Number(draft.width_mm),
  height_mm: Number(draft.height_mm),
});

function ModuleFactsLine({ input }: { input: TenantModuleInput }) {
  const efficiency = formatEfficiency(input.watts, input.width_mm, input.height_mm);
  if (!efficiency) return null;
  return (
    <p className="text-xs text-muted-foreground">
      {physicalSize(input.width_mm, input.height_mm)} · {efficiency} efficient
    </p>
  );
}

function ModuleForm({
  initial,
  saving,
  onSave,
  onCancel,
}: {
  initial: DraftFields;
  saving: boolean;
  onSave: (input: TenantModuleInput) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<DraftFields>(initial);
  const [errors, setErrors] = useState<ModuleInputErrors>({});

  const set = (field: keyof DraftFields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [field]: e.target.value }));

  const submit = () => {
    const input = toModuleInput(draft);
    const found = validateModuleInput(input);
    setErrors(found);
    if (Object.keys(found).length === 0) onSave(input);
  };

  const fieldError = (field: keyof ModuleInputErrors) =>
    errors[field] ? <p className="text-xs text-destructive">{errors[field]}</p> : null;

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="module-manufacturer">Manufacturer</Label>
          <Input id="module-manufacturer" value={draft.manufacturer} onChange={set('manufacturer')} disabled={saving} />
          {fieldError('manufacturer')}
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-model">Model code</Label>
          <Input id="module-model" value={draft.model} onChange={set('model')} disabled={saving} placeholder="LR5-54HTH-420M" />
          {fieldError('model')}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="module-watts">Watts</Label>
          <div className="flex items-center gap-2">
            <Input id="module-watts" type="text" inputMode="numeric" value={draft.watts} onChange={set('watts')} disabled={saving} />
            <span className="text-sm text-muted-foreground">W</span>
          </div>
          {fieldError('watts')}
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-width">Width</Label>
          <div className="flex items-center gap-2">
            <Input id="module-width" type="text" inputMode="numeric" value={draft.width_mm} onChange={set('width_mm')} disabled={saving} />
            <span className="text-sm text-muted-foreground">mm</span>
          </div>
          {fieldError('width_mm')}
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-height">Height</Label>
          <div className="flex items-center gap-2">
            <Input id="module-height" type="text" inputMode="numeric" value={draft.height_mm} onChange={set('height_mm')} disabled={saving} />
            <span className="text-sm text-muted-foreground">mm</span>
          </div>
          {fieldError('height_mm')}
        </div>
      </div>
      {/* The audience thinks in both: derived facts render live beside the mm inputs. */}
      <ModuleFactsLine input={toModuleInput(draft)} />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save module
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function ManageModulesDialog({
  open,
  onOpenChange,
  canManage,
  onCatalogueChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  onCatalogueChanged: () => void;
}) {
  const [modules, setModules] = useState<PvModuleRow[] | null>(null);
  /** null = list; 'new' = add form; a row id = edit form. */
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setModules(await listModulesForManagement());
  }, []);

  useEffect(() => {
    if (!open) return;
    setEditing(null);
    setConfirmingDeactivate(null);
    void load();
  }, [open, load]);

  const afterWrite = async () => {
    await load();
    onCatalogueChanged();
  };

  const handleSave = async (input: TenantModuleInput) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await createTenantModule(input);
        toast.success('Module added. Choose it as the default on the settings screen if new designs should use it.');
      } else if (editing) {
        await updateTenantModule(editing, input);
        toast.success('Module updated. Designs that already exist keep the values they were drawn with.');
      }
      setEditing(null);
      await afterWrite();
    } catch (error) {
      console.error('[ManageModulesDialog] Failed to save module:', error);
      toast.error(saveErrorMessage(error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (row: PvModuleRow, isActive: boolean) => {
    setSaving(true);
    try {
      await setModuleActive(row.id, isActive);
      setConfirmingDeactivate(null);
      await afterWrite();
    } catch (error) {
      console.error('[ManageModulesDialog] Failed to change module state:', error);
      toast.error(saveErrorMessage(error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage modules</DialogTitle>
          <DialogDescription>
            The catalogue your designs pick from. The default for new designs is chosen on the
            settings screen, not here.
          </DialogDescription>
        </DialogHeader>

        {!canManage && (
          <p className="text-sm text-muted-foreground">{EMPTY_AND_ERROR.save_no_permission}</p>
        )}

        {modules === null ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {modules.map((m) =>
              editing === m.id ? (
                <ModuleForm
                  key={m.id}
                  initial={draftFromRow(m)}
                  saving={saving}
                  onSave={handleSave}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div
                  key={m.id}
                  className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${m.is_active ? '' : 'opacity-60'}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {m.manufacturer} {m.model}
                      {!m.is_active && (
                        <Badge variant="outline" className="ml-2 text-muted-foreground">
                          Inactive
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {m.watts} W · {physicalSize(m.width_mm, m.height_mm)} ·{' '}
                      {formatEfficiency(m.watts, m.width_mm, m.height_mm)} efficient
                    </p>
                    {confirmingDeactivate === m.id && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-muted-foreground">{DEACTIVATE_REASON}</p>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" disabled={saving} onClick={() => handleSetActive(m, false)}>
                            Deactivate
                          </Button>
                          <Button size="sm" variant="ghost" disabled={saving} onClick={() => setConfirmingDeactivate(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge
                      variant="outline"
                      className={m.scope === 'global' ? 'text-muted-foreground border-dashed' : 'text-foreground'}
                    >
                      {m.scope === 'global' ? 'Shipped' : 'Yours'}
                    </Badge>
                    {/* Tenant rows only: no edit path to Shipped rows exists in the UI, because
                        none exists in the database — a button that will 42501 is not a control. */}
                    {canManage && m.scope === 'tenant' && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          disabled={saving}
                          onClick={() => setEditing(m.id)}
                          aria-label={`Edit ${m.manufacturer} ${m.model}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {m.is_active ? (
                          confirmingDeactivate !== m.id && (
                            <Button size="sm" variant="ghost" disabled={saving} onClick={() => setConfirmingDeactivate(m.id)}>
                              Deactivate
                            </Button>
                          )
                        ) : (
                          <Button size="sm" variant="ghost" disabled={saving} onClick={() => handleSetActive(m, true)}>
                            Reactivate
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            )}

            {editing === 'new' ? (
              <ModuleForm initial={EMPTY_DRAFT} saving={saving} onSave={handleSave} onCancel={() => setEditing(null)} />
            ) : (
              canManage && (
                <Button variant="outline" size="sm" disabled={saving} onClick={() => setEditing('new')}>
                  <Plus className="mr-2 h-4 w-4" /> Add module
                </Button>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
